/** @format */
import isPropValid from '@emotion/is-prop-valid';
import React, {
    ForwardRefRenderFunction,
    ReactNode,
    Ref,
    forwardRef,
} from 'react';
import styled from 'styled-components';

import { styles } from './icon-button.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum IconButtonFill {
    SOLID = 'solid',
    HOLLOW = 'hollow',
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum IconButtonSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large',
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum IconButtonTheme {
    MIDNIGHT = 'midnight',
    WHITE = 'white',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface IconButtonProps {
    /**
     * Whether the border is visible
     * @default true
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    border?: boolean;

    /**
     * The fill style, one of:
     * 1. SOLID
     * 2. HOLLOW
     * @default SOLID
     */
    fill?: IconButtonFill;

    /**
     * The size of the icon button, one of:
     * 1. SMALL
     * 2. MEDIUM
     * 3. LARGE
     * 4. EXTRA_LARGE
     *
     * @default MEDIUM
     */
    size?: IconButtonSize;

    /**
     * The theme of the icon button, one of:
     * 1. MIDNIGHT
     * 2. WHITE
     *
     * @default MIDNIGHT
     */
    iconButtonTheme?: IconButtonTheme;

    /**
     * Whether the icon button is disabled
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    disabled?: boolean;

    /**
     * Link URL
     */
    href?: string;

    /**
     * The screen reader label
     */
    label: string;

    /**
     * A title for the icon button. to be displayed in the DOM
     */
    title?: string;

    /**
     * Whether to show the label or set it as the aria-label attribute
     *
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    displayLabel?: boolean;

    /**
     * Force the input to show an invalid state without having to pass
     * an error message to display.
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    forceError?: boolean;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Specify the button type
     *
     * @default button
     */
    // eslint-disable-next-line @mpb-com/mpb/prefer-enum-over-string-union -- Legacy code: do not use as an example.
    type?: 'submit' | 'reset' | 'button';

    /**
     * Custom className
     */
    className?: string;

    /**
     * An optional tabindex parmeter for the html element
     */
    tabIndex?: number;

    /**
     * The icon to display
     */
    children?: ReactNode;

    /**
     * A custom aria-controls attribute
     */
    'aria-controls'?: string;

    /**
     * Mouse click event handler.
     *
     * @param event {MouseEvent<HTMLElement>} The mouse event
     */
    onClick?: (event: React.MouseEvent<HTMLElement>) => void;

    /**
     * Element blur event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The blur event
     */
    onBlur?: (event: React.FocusEvent<HTMLElement>) => void;

    /**
     * Element focus event handler.
     *
     * @param event {FocusEvent<HTMLElement>} The focus event
     */
    onFocus?: (event: React.FocusEvent<HTMLElement>) => void;
}

const BaseElementWithRef = (
    { href, children, type, ...otherProps }: IconButtonProps,
    ref: Ref<HTMLElement>
) => {
    const elementType = href ? 'a' : 'button';
    return React.createElement(
        elementType,
        {
            href,
            ref,
            type: elementType === 'button' ? type : undefined,
            ...otherProps,
        },
        children
    );
};

const blacklistedProps = ['fill', 'label'];
const StyledIconButton = styled(forwardRef(BaseElementWithRef)).withConfig({
    shouldForwardProp: (prop) =>
        isPropValid(prop) && !blacklistedProps.includes(prop),
})`
    ${styles}
`;

const IconButtonWithRef: ForwardRefRenderFunction<
    HTMLElement,
    IconButtonProps
> = (
    {
        border = true,
        fill = IconButtonFill.SOLID,
        size = IconButtonSize.MEDIUM,
        iconButtonTheme = IconButtonTheme.MIDNIGHT,
        disabled = false,
        displayLabel = false,
        href,
        label,
        title,
        children,
        type = 'button',
        ...otherProps
    }: IconButtonProps,
    ref: Ref<HTMLElement>
) => {
    return (
        <StyledIconButton
            border={border}
            fill={fill}
            size={size}
            iconButtonTheme={iconButtonTheme}
            disabled={disabled}
            href={disabled ? undefined : href}
            label={label}
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- Legacy code: do not use as an example.
            title={title ? title : label}
            aria-label={displayLabel ? undefined : label}
            ref={ref}
            type={type}
            {...otherProps}
        >
            <span className="icon">{children}</span>
            {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
            {displayLabel && <span className="label">{label}</span>}
        </StyledIconButton>
    );
};

export const IconButton = forwardRef(IconButtonWithRef);
